/* global trapFocus */

/**
 * EM-266 · Cart warranty offer (#9).
 *
 * A paid warranty added in the cart is its own top-level cart line, linked to
 * its PC by hidden properties (Shopify rejects nested lines under PCs with a
 * paid Easify option):
 *   - PC line:       `_wty_ref`  short random id
 *   - warranty line: `_wty_for`  the PC's `_wty_ref`
 *                    `_wty_prev` the PC's original warranty property value
 *                    `_wty_prev_key` that property's key, restored on Remove
 *                    `For`       visible: the PC's title, so checkout shows
 *                                which PC the warranty covers (key from
 *                                locales, `data-for-key`; our cart UI hides it
 *                                because it draws the line under its PC)
 * m-cart-items.liquid draws the warranty under its PC; this file runs the
 * actions (add Silver, upgrade to Gold, remove), keeps warranty quantities in
 * step with their PC (reconcile) and opens the "What's covered" sheet.
 *
 * The PC is either a prebuilt (Easify property "Warranty Upgrade") or a
 * configurator build (`_build_id`, property "Warranty"; its price is fixed
 * server-side by the build, so rewriting its properties never changes it).
 *
 * Easify's `_tpo_*` properties and the build's `_build_id` are copied through
 * untouched (EM-279).
 */
(() => {
  if (customElements.get('m-cart-warranty')) return;

  const WTY_KEYS = ['Warranty Upgrade', '_Warranty Upgrade'];
  const BUILD_WTY_KEYS = ['Warranty'];
  const BUILD_ID = '_build_id';
  const REF = '_wty_ref';
  const FOR = '_wty_for';
  const PREV = '_wty_prev';
  const PREV_KEY = '_wty_prev_key';

  const DEBUG = !!(window.Shopify && Shopify.theme && Shopify.theme.role !== 'main');
  const mLog = (...args) => {
    if (DEBUG) console.debug('[m-cart-warranty]', ...args); // eslint-disable-line no-console
  };

  const routes = () => ({
    cart: `${(theme.routes && theme.routes.cart) || '/cart'}.js`,
    add: `${(theme.routes && theme.routes.cartAdd) || '/cart/add'}.js`,
    change: `${(theme.routes && theme.routes.cartChange) || '/cart/change'}.js`,
    update: `${(theme.routes && theme.routes.cartUpdate) || '/cart/update'}.js`,
  });

  /**
   * POSTs JSON to a Cart Ajax API endpoint.
   * @param {string} url - Endpoint.
   * @param {object} body - Request body.
   * @returns {Promise<object>} Parsed response.
   */
  async function post(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || `HTTP ${response.status}`);
    }
    return data;
  }

  /**
   * Reads the current cart. Line keys change whenever properties change, so
   * every action starts from a fresh read.
   * @returns {Promise<object>}
   */
  async function getCart() {
    const response = await fetch(routes().cart, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  const props = (line) => line.properties || {};

  /**
   * @param {object} line - Cart line.
   * @returns {boolean} Whether the line is a configurator build.
   */
  const isBuild = (line) => Boolean(props(line)[BUILD_ID]);

  /**
   * @param {object} line - The PC cart line.
   * @returns {string[]} The property keys that carry its included warranty tier.
   */
  const warrantyKeys = (line) => (isBuild(line) ? BUILD_WTY_KEYS : WTY_KEYS);

  /**
   * @param {object} line - Cart line.
   * @returns {{key: string, value: string}|null} The PC's warranty property
   *   (Easify's on a prebuilt, the configurator's on a build).
   */
  function warrantyProperty(line) {
    const p = props(line);
    const key = warrantyKeys(line).find((k) => p[k]);
    return key ? { key, value: p[key] } : null;
  }

  // Keep the visible "For" value short: the variant title is only appended
  // when it is meaningful and short enough not to bloat the checkout line.
  const FOR_VARIANT_MAX = 30;

  // Build parts are full product titles; keep only the model so the "For"
  // value stays one short line at checkout.
  const FOR_PART_MAX = 24;
  const clip = (text, max) => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text);

  /**
   * @param {string} value - The build's "Graphics card" property, e.g.
   *   "ASUS GeForce RTX 5060 8GB Dual OC".
   * @returns {string} The GPU model, e.g. "RTX 5060".
   */
  function shortGpu(value) {
    const first = (value || '').split(',')[0].trim();
    const model = first.match(
      /\b(?:RTX|GTX|RX|Arc)\s+[A-Z]?\d{3,4}[A-Z]?(?:\s+(?:Ti|SUPER|Super|XTX|XT|GRE))*/
    );
    return clip(model ? model[0] : first, FOR_PART_MAX);
  }

  /**
   * @param {string} value - The build's "CPU" property, e.g. "AMD Ryzen 5 8400F".
   * @returns {string} The CPU without its brand, e.g. "Ryzen 5 8400F".
   */
  function shortCpu(value) {
    const first = (value || '').split(',')[0].trim();
    return clip(first.replace(/^(?:AMD|Intel)\s+/i, ''), FOR_PART_MAX);
  }

  /**
   * The visible "For" value for a warranty line. Read from cart line data,
   * not the DOM.
   * - Prebuilt: the product title, plus " — <variant title>" when the variant
   *   title is meaningful and short.
   * - Build: "<product title> — <GPU> / <CPU>" from the build's properties.
   *   Its variant title is a 64-character fingerprint, so it is never used.
   * @param {object} pc - The PC cart line.
   * @returns {string}
   */
  function forValue(pc) {
    const title = (pc.product_title || '').trim();
    if (isBuild(pc)) {
      const parts = [shortGpu(props(pc)['Graphics card']), shortCpu(props(pc).CPU)].filter(Boolean);
      return parts.length ? `${title} — ${parts.join(' / ')}` : title;
    }
    const variant = (pc.variant_title || '').trim();
    if (
      variant &&
      variant !== 'Default Title' &&
      variant.length <= FOR_VARIANT_MAX &&
      !title.includes(variant)
    ) {
      return `${title} — ${variant}`;
    }
    return title;
  }

  const newRef = () =>
    (Date.now().toString(36).slice(-4) + Math.random().toString(36).slice(2, 6)).slice(0, 8);

  /**
   * Analytics helper: pushes to dataLayer (if present) and fires a theme-style
   * custom event, `on:warranty-offer:<action>`.
   * @param {string} name - e.g. 'warranty_offer_add'.
   * @param {object} detail - { surface, pc_variant_id, pc_type, tier }.
   */
  function track(name, detail) {
    try {
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...detail });
    } catch {}
    document.dispatchEvent(
      new CustomEvent(`on:warranty-offer:${name.replace('warranty_offer_', '')}`, {
        bubbles: true,
        detail,
      })
    );
  }

  const controller = {
    busy: false,
    reconciling: false,
    lastReconcile: '',

    /**
     * Re-renders the cart drawer / cart page once, through the theme's own
     * cart-items section refresh (or a full drawer refresh when it empties).
     * @param {object} [cart] - Latest cart, if already fetched.
     */
    async refresh(cart) {
      const cartDrawer = document.getElementById('cart-drawer');
      const cartItems = document.querySelector('cart-items');

      if (cart && cart.item_count === 0) {
        if (cartDrawer && typeof cartDrawer.refresh === 'function') {
          await cartDrawer.refresh(true);
        } else {
          window.location.reload();
        }
        return;
      }

      if (cartItems && typeof cartItems.refresh === 'function') {
        await cartItems.refresh();
        if (cart) cartItems.currentTotalItemCount = cart.item_count;
      } else {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      }
    },

    /**
     * Brings warranty lines in line with their PCs: orphans (no matching PC)
     * and duplicates go to 0, quantities follow the PC. One update.js call, only
     * when something is wrong.
     */
    async reconcile() {
      if (this.busy || this.reconciling) return;
      this.reconciling = true;
      try {
        const cart = await getCart();
        const pcs = new Map();
        cart.items.forEach((line) => {
          const ref = props(line)[REF];
          if (ref) pcs.set(ref, line);
        });

        const updates = {};
        const seen = new Set();
        cart.items.forEach((line) => {
          const ref = props(line)[FOR];
          if (!ref) return;
          const pc = pcs.get(ref);
          if (!pc || seen.has(ref)) {
            updates[line.key] = 0;
          } else if (line.quantity !== pc.quantity) {
            updates[line.key] = pc.quantity;
          }
          seen.add(ref);
        });

        if (!Object.keys(updates).length) {
          this.lastReconcile = '';
          return;
        }

        // Never retry the identical correction twice in a row (no loops).
        const signature = JSON.stringify(updates);
        if (signature === this.lastReconcile) return;
        this.lastReconcile = signature;

        mLog('reconcile', updates);
        // Keep any line error on screen (e.g. "Only 5 items were added")
        // across the re-render. Error slots are keyed by line number, so only
        // when no line is removed (removing one renumbers the lines after it).
        const removesLine = Object.values(updates).includes(0);
        const errors = removesLine
          ? []
          : Array.from(document.querySelectorAll('.cart-item__error:not([hidden])'))
              .filter((el) => el.id && el.textContent.trim())
              .map((el) => [el.id, el.textContent]);
        const updated = await post(routes().update, { updates });
        await this.refresh(updated);
        errors.forEach(([id, text]) => {
          const el = document.getElementById(id);
          if (!el) return;
          el.textContent = text;
          el.hidden = false;
        });
      } catch (error) {
        console.error('[m-cart-warranty] reconcile failed', error);
      } finally {
        this.reconciling = false;
      }
    },

    /**
     * Bronze → Silver: tag the PC with `_wty_ref` (dropping the Bronze
     * "Warranty Upgrade" property), then add Silver linked to it.
     * @param {object} opts - { pcKey, silverId, forKey }.
     */
    async addSilver({ pcKey, silverId, forKey }) {
      const cart = await getCart();
      const pc = cart.items.find((line) => line.key === pcKey);
      if (!pc) throw new Error('PC line not found');

      // Resend every other property unchanged (incl. `_build_id`, `_tpo_*`).
      const original = { ...props(pc) };
      const warranty = warrantyProperty(pc);
      const ref = newRef();
      const pcProps = { ...original, [REF]: ref };
      warrantyKeys(pc).forEach((k) => delete pcProps[k]);

      const changed = await post(routes().change, {
        id: pc.key,
        quantity: pc.quantity,
        properties: pcProps,
      });

      const wtyProps = { [FOR]: ref, [PREV]: warranty ? warranty.value : '' };
      if (warranty) wtyProps[PREV_KEY] = warranty.key;
      const covers = forValue(pc);
      if (forKey && covers) wtyProps[forKey] = covers;

      try {
        await post(routes().add, {
          items: [{ id: Number(silverId), quantity: pc.quantity, properties: wtyProps }],
        });
      } catch (error) {
        // Revert the PC so it keeps its Bronze property and shows the offer again.
        const tagged = changed.items.find((line) => props(line)[REF] === ref);
        if (tagged) {
          await post(routes().change, {
            id: tagged.key,
            quantity: tagged.quantity,
            properties: original,
          }).catch((revertError) => console.error('[m-cart-warranty] revert failed', revertError));
        }
        throw error;
      }
    },

    /**
     * Silver → Gold: add Gold with the same link, then drop Silver. The cart is
     * re-rendered only after both, so it never shows two warranties.
     * @param {object} opts - { wtyKey, goldId, forKey }.
     */
    async upgrade({ wtyKey, goldId, forKey }) {
      const cart = await getCart();
      const silver = cart.items.find((line) => line.key === wtyKey);
      if (!silver) throw new Error('Warranty line not found');

      const linkProps = {};
      [FOR, PREV, PREV_KEY, forKey].forEach((k) => {
        if (k && props(silver)[k] !== undefined) linkProps[k] = props(silver)[k];
      });
      // Silver added before the "For" property existed: fill it from the PC.
      if (forKey && !linkProps[forKey]) {
        const pc = cart.items.find((line) => props(line)[REF] === linkProps[FOR]);
        const covers = pc ? forValue(pc) : '';
        if (covers) linkProps[forKey] = covers;
      }

      await post(routes().add, {
        items: [{ id: Number(goldId), quantity: silver.quantity, properties: linkProps }],
      });
      await post(routes().change, { id: silver.key, quantity: 0 });
    },

    /**
     * Remove: drop the warranty line, then untag the PC and restore its
     * original warranty property under the key it was removed from
     * (`_wty_prev_key`; lines added before that existed fall back to the
     * PC type's default key, "Warranty Upgrade" on a prebuilt).
     * @param {object} opts - { wtyKey, pcRef }.
     */
    async remove({ wtyKey, pcRef }) {
      const cart = await getCart();
      const warranty =
        cart.items.find((line) => line.key === wtyKey) ||
        cart.items.find((line) => props(line)[FOR] === pcRef);
      const ref = (warranty && props(warranty)[FOR]) || pcRef;
      const prev = warranty ? props(warranty)[PREV] : '';
      const storedKey = warranty ? props(warranty)[PREV_KEY] : '';

      let latest = cart;
      if (warranty) {
        latest = await post(routes().change, { id: warranty.key, quantity: 0 });
      }

      const pc = latest.items.find((line) => props(line)[REF] === ref);
      if (!pc) return;

      const prevKey = storedKey || warrantyKeys(pc)[0];
      const pcProps = { ...props(pc) };
      delete pcProps[REF];
      if (prev) pcProps[prevKey] = prev;
      await post(routes().change, { id: pc.key, quantity: pc.quantity, properties: pcProps });
    },
  };

  window.mCartWarranty = controller;

  /**
   * <m-cart-warranty>: one offer card or linked warranty line under a PC.
   */
  class CartWarranty extends HTMLElement {
    connectedCallback() {
      this.clickHandler = this.handleClick.bind(this);
      this.addEventListener('click', this.clickHandler);

      this.connector = this.querySelector('.m-cart-warranty__connector');
      if (this.connector) {
        this.cartItem = this.closest('.cart-item');
        this.measureHandler = () => window.requestAnimationFrame(() => this.drawConnector());
        this.measureHandler();
        if ('ResizeObserver' in window && this.cartItem) {
          this.resizeObserver = new ResizeObserver(this.measureHandler);
          this.resizeObserver.observe(this.cartItem);
        }
        const image = this.cartItem && this.cartItem.querySelector('.cart-item__image img');
        if (image && !image.complete) image.addEventListener('load', this.measureHandler);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.clickHandler);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    get surface() {
      return this.dataset.surface || 'drawer';
    }

    /** @returns {'prebuilt'|'build'} The PC this offer is for. */
    get pcType() {
      return this.dataset.pcType === 'build' ? 'build' : 'prebuilt';
    }

    /**
     * Draws the connector from under the PC image to the warranty thumb.
     */
    drawConnector() {
      const image =
        this.cartItem &&
        (this.cartItem.querySelector('.cart-item__image img') ||
          this.cartItem.querySelector('.cart-item__image'));
      const thumb = this.querySelector('.m-cart-warranty__thumb');
      if (!image || !thumb || !this.isConnected) return;

      const host = this.getBoundingClientRect();
      const img = image.getBoundingClientRect();
      const tb = thumb.getBoundingClientRect();
      const x0 = img.left + img.width / 2;
      const y0 = img.bottom + 6;
      const x1 = tb.left - 6;
      const y1 = tb.top + tb.height / 2;
      const width = x1 - x0;
      const height = y1 - y0;
      const visible = width > 8 && height > 8;

      this.connector.classList.toggle('is-visible', visible);
      if (!visible) return;
      Object.assign(this.connector.style, {
        left: `${x0 - host.left}px`,
        top: `${y0 - host.top}px`,
        width: `${width}px`,
        height: `${height}px`,
      });
    }

    /**
     * @param {object} evt - Click event.
     */
    handleClick(evt) {
      const button = evt.target.closest('[data-wty-action]');
      if (!button || !this.contains(button)) return;
      evt.preventDefault();

      const { wtyAction } = button.dataset;
      if (wtyAction === 'covered') {
        this.openSheet(button);
        return;
      }
      this.run(wtyAction, button);
    }

    /**
     * Runs a cart action with every warranty control disabled, then refreshes
     * the cart once.
     * @param {string} action - 'add' | 'upgrade' | 'remove'.
     * @param {Element} button - The pressed control.
     */
    async run(action, button) {
      if (controller.busy) return;
      controller.busy = true;

      const label = button.querySelector('.m-cart-warranty__btn-label');
      const originalLabel = label ? label.textContent : '';
      const controls = document.querySelectorAll('m-cart-warranty [data-wty-action]');
      controls.forEach((control) => {
        control.disabled = true;
      });
      document.querySelectorAll('m-cart-warranty').forEach((el) => el.classList.add('is-busy'));
      button.classList.add('m-cart-warranty__btn--loading');
      button.setAttribute('aria-busy', 'true');
      if (label && button.dataset.loadingLabel) label.textContent = button.dataset.loadingLabel;
      this.setError('');

      const detail = {
        surface: this.surface,
        pc_variant_id: Number(this.dataset.pcVariantId) || null,
        pc_type: this.pcType,
        tier: action === 'upgrade' ? 'gold' : this.dataset.tier,
      };

      let succeeded = false;
      try {
        if (action === 'add') {
          await controller.addSilver({
            pcKey: this.dataset.pcKey,
            silverId: this.dataset.silverId,
            forKey: this.dataset.forKey,
          });
        } else if (action === 'upgrade') {
          await controller.upgrade({
            wtyKey: this.dataset.wtyKey,
            goldId: this.dataset.goldId,
            forKey: this.dataset.forKey,
          });
        } else if (action === 'remove') {
          await controller.remove({ wtyKey: this.dataset.wtyKey, pcRef: this.dataset.pcRef });
        }
        succeeded = true;
        track(`warranty_offer_${action}`, detail);
      } catch (error) {
        console.error('[m-cart-warranty]', action, error);
      }

      try {
        const cart = await getCart();
        await controller.refresh(cart);
      } catch (error) {
        console.error('[m-cart-warranty] refresh failed', error);
      } finally {
        controller.busy = false;
      }

      // The refresh replaces this element on success; on failure it may survive
      // (or be re-rendered), so surface the error on whatever is now in place.
      if (!succeeded) {
        const target = this.isConnected ? this : this.findReplacement();
        if (target) {
          target.querySelectorAll('[data-wty-action]').forEach((control) => {
            control.disabled = false;
          });
          target.setError(target.dataset.errorText || '');
        }
        if (this.isConnected) {
          button.classList.remove('m-cart-warranty__btn--loading');
          button.removeAttribute('aria-busy');
          if (label) label.textContent = originalLabel;
        }
      }
      document.querySelectorAll('m-cart-warranty').forEach((el) => el.classList.remove('is-busy'));
    }

    /**
     * @returns {CartWarranty|null} The element rendered for the same PC after a
     * refresh.
     */
    findReplacement() {
      const id = this.dataset.pcVariantId;
      return document.querySelector(`m-cart-warranty[data-pc-variant-id="${id}"]`);
    }

    /**
     * @param {string} message - Error to show ('' hides it).
     */
    setError(message) {
      const el = this.querySelector('.m-cart-warranty__error');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    /**
     * Opens the read-only "What's covered" sheet on this tier's panel.
     * @param {Element} opener - The pressed link.
     */
    openSheet(opener) {
      const sheet = document.getElementById('m-cart-warranty-sheet');
      if (!sheet || typeof sheet.open !== 'function') return;

      const tier = this.dataset.tier;
      const panels = sheet.querySelectorAll('[data-wty-sheet-tier]');
      const match =
        sheet.querySelector(`[data-wty-sheet-tier="${tier}"]`) ||
        sheet.querySelector('[data-wty-sheet-tier="other"]');
      panels.forEach((panel) => {
        panel.hidden = panel !== match;
      });

      const dialog = sheet.querySelector('[role="dialog"]');
      const title = match && match.querySelector('[id^="m-cart-warranty-sheet-title"]');
      if (dialog && title) dialog.setAttribute('aria-labelledby', title.id);

      // Hand focus back to the drawer (its trap is removed when the sheet
      // closes) once the sheet is dismissed.
      if (!sheet.dataset.mWtyPatched) {
        sheet.dataset.mWtyPatched = 'true';
        const close = sheet.close.bind(sheet);
        sheet.close = () => {
          close();
          const drawer = document.querySelector('cart-drawer[open]');
          if (drawer && typeof trapFocus === 'function') {
            trapFocus(drawer, sheet.openedBy && sheet.openedBy.isConnected ? sheet.openedBy : null);
          }
        };
      }

      sheet.open(opener);
      if (dialog) dialog.focus();

      track('warranty_offer_whats_covered', {
        surface: this.surface,
        pc_variant_id: Number(this.dataset.pcVariantId) || null,
        pc_type: this.pcType,
        tier,
      });
    }
  }

  customElements.define('m-cart-warranty', CartWarranty);

  /**
   * <m-cart-warranty-reconcile>: rendered by m-cart-items.liquid when a
   * warranty line is orphaned, duplicated or out of step with its PC.
   */
  class CartWarrantyReconcile extends HTMLElement {
    connectedCallback() {
      window.setTimeout(() => controller.reconcile(), 0);
    }
  }

  customElements.define('m-cart-warranty-reconcile', CartWarrantyReconcile);

  // Reconcile after any cart change made outside these actions. A quantity
  // capped by stock fires on:cart:error, not on:line-item:change, although
  // Shopify has still applied the capped quantity to the PC.
  const scheduleReconcile = () => window.setTimeout(() => controller.reconcile(), 50);
  ['on:line-item:change', 'on:cart:add', 'on:cart:error', 'on:cart-drawer:after-open'].forEach(
    (name) => {
      document.addEventListener(name, scheduleReconcile);
    }
  );
})();
